/**
 * @module lib/oauthReturnTo
 *
 * Deciding whether a `returnTo` handed to the login page may be followed.
 *
 * When Hydra starts a handshake and finds nobody signed in, the backend bounces
 * the browser to the SPA's login page carrying `?returnTo=<issuer>/oauth/login…`
 * so the user lands back inside the handshake after authenticating. That makes
 * the login page an open-redirect surface: whatever ends up in that parameter is
 * attacker-controlled, and following it blindly turns our own login form into a
 * credible launch pad for a phishing page.
 *
 * The rule here is deliberately narrow — an absolute URL, on exactly the API
 * origin, at exactly one of the API's own `/oauth/login` or `/oauth/consent`
 * paths, the only handshake endpoints the backend serves. Everything else is
 * dropped and the user goes to the home page, which is a harmless outcome for
 * a legitimate visitor and the only safe one for a crafted link.
 *
 * Every function here takes the API *base* (`API_ROOT`), not a path-stripped
 * origin: it is the same value `buildRootUrl` builds real requests on, so the
 * URL we send the browser to and the URL a request would reach cannot drift
 * apart when `VITE_API_URL` carries a path prefix.
 */

/** The handshake endpoints a `returnTo` may name, relative to the API base. */
export const OAUTH_RETURN_PATHS = Object.freeze(['/oauth/login', '/oauth/consent']);

/**
 * A path under the API base.
 *
 * With `VITE_API_URL=https://api.example.com` `/oauth/login` stays as it is;
 * with `https://api.example.com/ms` it is `/ms/oauth/login`, which is where the
 * authorization endpoints actually are.
 *
 * @param {URL} base Parsed API base
 * @param {string} path Path under the base, leading slash optional
 * @returns {string}
 */
function underBase(base, path) {
  const basePath = base.pathname.replace(/\/+$/, '');
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return `${basePath}${suffix}`;
}

/**
 * Extract a `returnTo` from a query string, if and only if it is safe to follow.
 *
 * The URL is parsed with no base, so relative and scheme-relative values
 * (`//evil.example/x`) fail to parse instead of resolving against our own origin
 * and inheriting its trust. Origins are then compared as origins rather than by
 * string prefix: `https://api.example.com.evil.test` starts with
 * `https://api.example.com` but is a different site.
 *
 * @param {string} search Raw query string, e.g. `window.location.search`
 * @param {string} apiBase Absolute base of the API host (`API_ROOT`), path and all
 * @param {readonly string[]} [allowedPaths] Exact paths under that base the target may name
 * @returns {string|null} The URL to follow, or null when it must be ignored
 */
export function safeOAuthReturnTo(search, apiBase, allowedPaths = OAUTH_RETURN_PATHS) {
  if (!search || !apiBase) return null;

  const raw = new URLSearchParams(search).get('returnTo');
  if (!raw) return null;

  let target;
  let expected;
  try {
    target = new URL(raw);
    expected = new URL(apiBase);
  } catch (parseError) {
    return null;
  }

  if (target.origin !== expected.origin) return null;
  if (target.username || target.password || target.hash) return null;
  if (!allowedPaths.some((path) => target.pathname === underBase(expected, path))) return null;

  return target.href;
}

/**
 * Read the consent challenge out of the pre-hash query string.
 *
 * The router is hash-based, so its `querystring` store only ever sees what
 * follows the '#'. Hydra's challenge arrives *before* it — the backend redirects
 * to `<web>/?consent_challenge=…#/oauth/consent` — which is invisible to the
 * router and has to be read from `window.location.search` directly.
 *
 * @param {string} search Raw query string, e.g. `window.location.search`
 * @returns {string} The challenge, or '' when absent
 */
export function consentChallengeFrom(search) {
  if (!search) return '';
  return new URLSearchParams(search).get('consent_challenge') || '';
}

/**
 * Build the URL that resumes a consent handshake after signing in.
 *
 * It points at the backend's `/oauth/consent`, not straight back at the SPA
 * route: that endpoint re-reads the challenge from Hydra and bounces the browser
 * to the consent screen, so the flow is resumed by the authorization server
 * rather than reconstructed by guesswork in the client.
 *
 * Built on the API base rather than its origin, so it names the same URL a real
 * request reaches — `https://host/ms/oauth/consent`, not `https://host/oauth/consent`.
 *
 * @param {string} search Raw query string carrying `consent_challenge`
 * @param {string} apiBase Absolute base of the API host (`API_ROOT`), path and all
 * @returns {string|null} URL to hand to the login page, or null when there is
 *   no challenge to come back to
 */
export function consentResumeUrl(search, apiBase) {
  const challenge = consentChallengeFrom(search);
  if (!challenge || !apiBase) return null;

  const query = new URLSearchParams({ consent_challenge: challenge }).toString();
  return `${apiBase.replace(/\/+$/, '')}/oauth/consent?${query}`;
}

/**
 * The `href` of the consent screen's "back to login" link.
 *
 * The challenge lives in the pre-hash query string, so the link has to rebuild
 * that query rather than being a bare `#/login`: the hash router would otherwise
 * drop it and the user would sign in with the handshake left dangling. What it
 * carries is the resume URL, which Login re-validates with `safeOAuthReturnTo`
 * before following — so the two must agree on where the API lives, and both take
 * the same base to make sure they do.
 *
 * @param {string} search Raw query string carrying `consent_challenge`
 * @param {string} apiBase Absolute base of the API host (`API_ROOT`), path and all
 * @param {string} [pathname] Path the SPA is served from
 * @returns {string} A link to the login route, with the handshake attached when
 *   there is one to preserve
 */
export function consentLoginHref(search, apiBase, pathname = '/') {
  const resumeUrl = consentResumeUrl(search, apiBase);
  if (!resumeUrl) return '#/login';

  return `${pathname}?returnTo=${encodeURIComponent(resumeUrl)}#/login`;
}
